/**
 * Форматы чисел, дат и множественного числа.
 *
 * Все правила — reference/data-viz.md §8 и reference/voice.md дизайн-системы.
 * Ни одна поверхность не форматирует числа сама: разные округления на экране и
 * в выгрузке — это разговор с клиентом о том, почему у него два разных балла.
 *
 * Язык и рынок сюда не вписаны. `'ru-RU'` и `'Europe/Moscow'` стояли буквами в
 * каждом вызове `Intl` этого файла; теперь и то и другое приходит из активной
 * локали и настроек рынка — `shared/src/intl.ts` и `shared/src/i18n/market.ts`.
 * Пока рынок один, не меняется ничего: заглушка `RU_MARKET` переписана отсюда
 * до последнего неразрывного пробела.
 *
 * Слова, которые здесь всё ещё набраны по-русски, — `NOT_MEASURED`, ступени
 * `SCORE_LADDER` и `CONFIDENCE_LADDER`, тройки склонений в вызовах `countOf` —
 * уезжают в каталоги вместе с экранами, которые их зовут (B5). Переносить их
 * раньше вызывающих значит держать одну строку в двух местах сразу.
 */

import { numberFormat, dateTimeFormat, pluralRules } from './intl';
import { market } from './i18n/market';

/** Неразрывный пробел U+00A0. Никогда не обычный пробел внутри числа и перед единицей. */
export const NBSP = ' ';
/** Настоящий минус U+2212, а не дефис. Им пишутся все дельты. */
export const MINUS = '−';

/**
 * Целое число: разряды разделены неразрывным пробелом (9 900), запятая не используется.
 */
export function int(value: number): string {
  return numberFormat({ maximumFractionDigits: 0 }).format(value);
}

/**
 * Дробное число: разделитель — запятая (50,7). Точка — самый частый способ
 * русского интерфейса выдать в себе перевод.
 */
export function decimal(value: number, digits = 1): string {
  return numberFormat({
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/** Процент. Знак ставится вплотную к числу: 42%. Пробел рвёт колонку цифр. */
export function percent(value: number, digits = 0): string {
  return `${digits ? decimal(value, digits) : int(value)}%`;
}

/**
 * Деньги: 9 900 ₽. Знак валюты берётся у рынка, перед ним неразрывный пробел.
 *
 * Не `Intl.NumberFormat` со `style: 'currency'`: `ru-RU` печатает «9 900,00 ₽»
 * с копейками всегда, а дизайн-система показывает копейки только там, где они
 * есть (`data-viz.md` §8).
 *
 * Функция называлась `rubles()`. Имя пришлось бы оставить в коде, печатающем
 * дирхамы, — то есть завести ровно тот дефект, который весь этот файл и
 * предотвращает: одно и то же число, названное на двух экранах по-разному.
 */
export function money(value: number): string {
  return `${int(value)}${NBSP}${market().currencySymbol}`;
}

/** Балл индекса ИИ-видимости: 0–100 без единицы. */
export function score(value: number): string {
  return int(Math.round(value));
}

/**
 * Дельта со знаком. Минус — U+2212, плюс — обычный.
 * `unit` дописывается через неразрывный пробел: `п.${NBSP}п.` и подобные.
 */
export function delta(value: number, unit?: string, digits = 0): string {
  const body = digits ? decimal(Math.abs(value), digits) : int(Math.abs(value));
  const sign = value < 0 ? MINUS : '+';
  return unit ? `${sign}${body}${NBSP}${unit}` : `${sign}${body}`;
}

/** Дельта видимости в процентных пунктах: +5 п. п. */
export function deltaPoints(value: number, digits = 0): string {
  return delta(value, `п.${NBSP}п.`, digits);
}

/**
 * Дельта индекса ИИ-видимости: `+6`, **без единицы**.
 *
 * Единственная метрика продукта, у которой единицы нет вовсе (data-viz.md §8):
 * индекс — не проценты и не процентные пункты, поэтому ни «п. п.», ни «балла»
 * рядом с ним не ставится. Отдельная функция нужна не ради тела в одну строку,
 * а чтобы у формулировки было одно место: до неё «Обзор», «Динамика» и
 * «Клиенты» называли одно число тремя разными способами, и один из трёх ещё и
 * не склонялся («+11 балла»).
 *
 * Чем измерение сравнивается — прошлой точкой, прошлым окном — говорит
 * основание экрана, а не подпись у числа: правило основания экранное, а не
 * поштучное (data-viz.md §1).
 */
export function indexDelta(value: number): string {
  return delta(value);
}

/**
 * Множественное число. Три формы, никакой склейки строк.
 * Ноль берёт форму «многих» (0 движков) и это настоящий случай: спека §9
 * использует «0 упоминаний» как порог оповещения.
 * Дробные значения Intl.PluralRules не различает, поэтому у них своя ветка.
 */
export function pluralize(value: number, one: string, few: string, many: string): string {
  if (!Number.isInteger(value)) return few; // 1,5 балла · 0,5 пункта
  const form = pluralRules().select(value);
  if (form === 'one') return one;
  if (form === 'few') return few;
  return many;
}

/** Число со словом: `5 движков`, неразрывный пробел между ними. */
export function countOf(value: number, one: string, few: string, many: string): string {
  const num = Number.isInteger(value) ? int(value) : decimal(value);
  return `${num}${NBSP}${pluralize(value, one, few, many)}`;
}

/**
 * Дата в прозе, одним вызовом Intl.
 *
 * Две ловушки сразу, и обе видно только на русском.
 *
 * 1. **Месяц в родительном падеже.** В изоляции `month: 'long'` вернёт
 *    «август», а не «августа», поэтому строка собирается целиком и никогда не
 *    конкатенацией частей.
 * 2. **Хвост «г.».** `ru-RU` дописывает его к каждой дате с годом, а
 *    дизайн-система пишет `6 августа 2026` без него (data-viz.md §8). Срезаем
 *    по разбору на части, а не регулярным выражением по готовой строке: части
 *    посчитаны тем же одним вызовом, поэтому падеж месяца сохраняется.
 */
const prose = {
  format(d: Date): string {
    const parts = dateTimeFormat({
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).formatToParts(d);

    const end = parts.findIndex((part) => part.type === 'era');
    const kept = (end === -1 ? parts : parts.slice(0, end))
      .filter((part) => part.type !== 'literal' || !part.value.includes('г.'))
      .map((part) => part.value)
      .join('');

    return kept.trim();
  },
};

export function dateProse(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return prose.format(d);
}

/** Дата в таблице и в моноширинной подписи: 06.08.2026. */
export function dateNumeric(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return dateTimeFormat({
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * Дата на оси графика: «06.08». Года нет намеренно — он стоит один раз в
 * основании графика, а под точками пять раз подряд занял бы место, которого в
 * ячейке нет, и подписи наехали бы друг на друга.
 */
export function dateShort(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return dateTimeFormat({
    day: '2-digit',
    month: '2-digit',
  }).format(d);
}

/**
 * Период: «1–7 августа 2026». Тире короткое (U+2013) и без пробелов —
 * длинное тире диапазоном не бывает.
 */
export function period(from: Date | string, to: Date | string): string {
  const a = typeof from === 'string' ? new Date(from) : from;
  const b = typeof to === 'string' ? new Date(to) : to;
  const day = dateTimeFormat({ day: 'numeric' });
  const sameMonth =
    a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth();
  return sameMonth
    ? `${day.format(a)}–${prose.format(b)}`
    : `${prose.format(a)}–${prose.format(b)}`;
}

/** Охват движков: «5 из 9». Счёт, никогда не процент. */
export function coverage(done: number, total: number): string {
  return `${int(done)}${NBSP}из${NBSP}${int(total)}`;
}

/**
 * То же с существительным: «1 из 1 ответа», «3 из 3 ответов», «5 из 9 ответов».
 *
 * Экраны писали `${coverage(a, b)} ответов` — и при total 1, 21, 101 получалось
 * «назван в 1 из 1 ответов» и «Закрыто 20 из 21 задач». Дефект механический и
 * повторялся в шестнадцати местах.
 *
 * **Тройка здесь та же, что у `countOf`, — именительная**, и берётся из неё не
 * та форма. «Из» требует родительного падежа, числительное после него тоже
 * стоит в родительном, и существительное согласуется уже с ним:
 *
 * * `из 1 ответа`, `из 21 ответа` — родительный ЕДИНСТВЕННОГО. У `countOf` эта
 *   форма лежит в слоте `few` («2 ответа»), и она же нужна здесь;
 * * `из 2 ответов`, `из 3 ответов`, `из 9 ответов` — родительный МНОЖЕСТВЕННОГО,
 *   то есть слот `many`. Форма `few` («3 ответа») тут неверна: правильно «3 из 3
 *   ответов», а не «3 из 3 ответа».
 *
 * Поэтому правило одно: `one` по `Intl.PluralRules` → берём `few`, всё
 * остальное → `many`. Слот `one` не используется вовсе и оставлен только затем,
 * чтобы на поверхности стояла ровно та же тройка слов, что и у `countOf`, — две
 * разные тройки на одно существительное разъехались бы первой же правкой.
 */
export function coverageOf(
  done: number,
  total: number,
  one: string,
  few: string,
  many: string,
): string {
  void one;
  const word = pluralRules().select(total) === 'one' ? few : many;
  return `${coverage(done, total)}${NBSP}${word}`;
}

/**
 * Средняя позиция: `2,4`. Улучшение — уменьшение числа, поэтому знака у самого
 * показателя нет, а направление называется словом рядом (data-viz.md §8).
 */
export function position(value: number): string {
  return decimal(value, 1);
}

/**
 * Дельта средней позиции: `−0,3`. Минус здесь означает улучшение, и экран
 * обязан сказать это словом — стрелка вниз на «позиции» читается как падение.
 */
export function deltaPosition(value: number): string {
  return delta(value, undefined, 1);
}

/** Тональность −50…+50. Всегда со знаком: ноль тоже результат, а не пустота. */
export function sentiment(value: number): string {
  return delta(value);
}

/**
 * Дельта тональности: `+3`, **без единицы** и в тех же пунктах шкалы, что само
 * число.
 *
 * Показатель печатается `sentiment()` — знак и число без единицы, — а движение
 * рядом с ним печаталось «п. п.». Процентных пунктов у тональности нет вовсе:
 * шкала от −50 до +50 (контракт, `BrandTotals.sentiment_score`), и приписка
 * называла клиенту чужую единицу прямо рядом со своей.
 */
export function sentimentDelta(value: number): string {
  return delta(value);
}

/** Волатильность за неделю: `±4,1 п. п.`. Без знака направления — это разброс. */
export function volatility(value: number): string {
  return `±${decimal(value, 1)}${NBSP}п.${NBSP}п.`;
}

/**
 * Уверенность словом (data-viz.md §8: «высокая / средняя / низкая», доля
 * согласия — в подсказке). Значение в контракте от 0 до 1.
 *
 * design: derived, not documented — сами пороги ступеней в дизайн-системе не
 * записаны. Взяты те же, что у правил оповещений: ниже половины согласия двух
 * прогонов число считать устойчивым нельзя.
 */
export const CONFIDENCE_LADDER = [
  { min: 0.8, name: 'высокая' },
  { min: 0.5, name: 'средняя' },
  { min: 0, name: 'низкая' },
] as const;

export function confidenceWord(value: number): string {
  return (
    CONFIDENCE_LADDER.find((step) => value >= step.min) ?? CONFIDENCE_LADDER.at(-1)!
  ).name;
}

/** Доля согласия прогонов для подсказки: `86%`. */
export function confidenceShare(value: number): string {
  return percent(Math.round(value * 100));
}

/** Строка «не измерено». Никогда не «n/a», не пустая ячейка и не ноль. */
export const NOT_MEASURED = 'Не измерено';

/**
 * Лестница статусов индекса (спека §8). Ступени объявлены данными, а не
 * цепочкой if: витрине они нужны списком, кабинету — по значению, а два
 * описания одной лестницы разъедутся на первой же правке порогов.
 * Нижняя ступень 0–19 — самый частый итог аудита.
 */
export const SCORE_LADDER = [
  { min: 80, name: 'Доминирует' },
  { min: 60, name: 'Сильный' },
  { min: 40, name: 'Средний' },
  { min: 20, name: 'Ниже среднего' },
  { min: 0, name: 'Не виден' },
] as const;

/** Статус по баллу. Единственный способ получить слово из числа. */
export function statusLadder(value: number): string {
  return (SCORE_LADDER.find((step) => value >= step.min) ?? SCORE_LADDER.at(-1)!).name;
}

/**
 * Лестница строкой: «80+ Доминирует · … · 0–19 Не виден». Для подписи на витрине.
 *
 * Собирается из той же таблицы, по которой кабинет считает статус из числа.
 * Набирать её словами в разметке нельзя: клиент, увидевший на экране статус,
 * которого не было на лендинге, уже не верит ни экрану, ни лендингу.
 *
 * Две мелочи, из-за которых строка раньше расходилась с voice.md: статусы
 * пишутся с заглавной, и нижняя ступень **входит в перечисление** — 0–19 это
 * самый частый итог бесплатного аудита, и умалчивать про неё на витрине,
 * которая аудит и продаёт, нечестно. Нижняя ступень названа диапазоном, а не
 * «0+»: у неё есть верхняя граница, у остальных её нет.
 */
export function ladderProse(): string {
  const last = SCORE_LADDER.at(-1)!;
  const above = SCORE_LADDER.filter((step) => step.min > 0);
  const top = above.at(-1)!;
  return [
    ...above.map((step) => `${int(step.min)}+${NBSP}${step.name}`),
    `${int(last.min)}–${int(top.min - 1)}${NBSP}${last.name}`,
  ].join(' · ');
}

/**
 * Деньги из копеек: `990000` → `9 900 ₽`, `1499924` → `14 999,24 ₽`.
 *
 * В контракте деньги целые и в копейках — так их держат и биллинг, и база.
 * Экран обязан показывать рубли, и превращение делается ровно в одном месте:
 * делённое пополам округление в двух разных компонентах — это два разных счёта
 * на одну подписку.
 *
 * **Копейки не отбрасываются.** Здесь стояло `Math.round(value / 100)`, и это
 * прямо расходилось с чеком: пропорциональная доплата — единственное место, где
 * копейки не нулевые, и именно её кабинет показывал неверно. Приёмка списала
 * 14 999,24 ₽ и увидела в таблице платежей «14 999 ₽», а на второй доплате
 * округление ушло в другую сторону: 34 885,78 ₽ → «34 886 ₽». Клиент, сверяющий
 * кабинет с выпиской банка, видел другое число.
 *
 * Ровные суммы по-прежнему пишутся без хвоста: «9 900 ₽», а не «9 900,00 ₽» —
 * копейки показываются только там, где они есть.
 */
export function moneyKopecks(value: number): string {
  const negative = value < 0;
  const absolute = Math.abs(Math.trunc(value));
  const whole = Math.trunc(absolute / 100);
  const cents = absolute % 100;
  const sign = negative ? MINUS : '';
  if (cents === 0) return `${sign}${money(whole)}`;
  return `${sign}${int(whole)},${String(cents).padStart(2, '0')}${NBSP}${market().currencySymbol}`;
}

/**
 * Дата и время: `8 августа 2026, 12:20`. Нужны там, где важен момент, а не
 * день, — последний вход, отзыв сессии, момент списания.
 */
export function momentProse(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  const time = dateTimeFormat({
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
  return `${prose.format(d)}, ${time}`;
}

/**
 * Только часы и минуты: `14:32`.
 *
 * Для строк «показываем данные на 14:32» — когда дата не нужна, потому что речь
 * про эту же минуту, а полная фраза «7 августа, 14:32» в неблокирующей полосе
 * читается как что-то давнее. Пояс тот же московский, что у всех остальных
 * дат продукта: время замера считается по Москве.
 */
export function timeOfDay(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return dateTimeFormat({
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Сколько дней осталось. Ноль — «сегодня», отрицательное — «срок прошёл»:
 * «осталось −2 дня» в интерфейсе не пишется никогда.
 */
export function daysLeft(value: Date | string, from: Date = new Date()): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  const diff = Math.ceil((d.getTime() - from.getTime()) / 86400000);
  if (diff < 0) return 'срок прошёл';
  if (diff === 0) return 'сегодня';
  return `через ${countOf(diff, 'день', 'дня', 'дней')}`;
}

/**
 * Домен для глаз: `xn--h1aen.xn--p1ai` → `пик.рф`.
 *
 * В базе домен лежит в ASCII — так его нормализует API (`encode('idna')`), и
 * так его требует DNS. Показывать эту запись человеку нельзя: владелец завёл
 * бренд «Пик» и увидел на экране «Разбираем xn--h1aen», в котором не узнал ни
 * своего бренда, ни своего сайта.
 *
 * Свободный аудит уже раскодирован на сервере (поле `website_display`), а вот
 * бренды, конкуренты и переключатель контекста читают домен как есть — им и
 * нужна эта функция. Браузерного API для обратного преобразования нет:
 * `URL.domainToUnicode` живёт только в Node, поэтому декодер записан здесь.
 *
 * Реализация — RFC 3492, только раскодирование: кодировать нам нечего, домен
 * приходит уже нормализованным. Метка без префикса `xn--` возвращается как
 * есть, испорченная метка — тоже: неразобранный домен показать хуже, чем
 * ничего, но подставить вместо него пустоту хуже вдвойне.
 */
export function domainProse(domain: string | null | undefined): string {
  if (!domain) return '';
  return domain
    .split('.')
    .map((label) => (/^xn--/i.test(label) ? decodeLabel(label.slice(4)) : label))
    .join('.');
}

/** Одна метка punycode без префикса. Возвращает исходную строку, если не разобралась. */
function decodeLabel(input: string): string {
  const BASE = 36;
  const TMIN = 1;
  const TMAX = 26;
  const SKEW = 38;
  const DAMP = 700;
  const INITIAL_BIAS = 72;
  const INITIAL_N = 128;

  const adapt = (delta: number, count: number, first: boolean): number => {
    let d = first ? Math.floor(delta / DAMP) : delta >> 1;
    d += Math.floor(d / count);
    let k = 0;
    while (d > ((BASE - TMIN) * TMAX) >> 1) {
      d = Math.floor(d / (BASE - TMIN));
      k += BASE;
    }
    return k + Math.floor(((BASE - TMIN + 1) * d) / (d + SKEW));
  };

  // Пустая метка после префикса — это не punycode, а обрубок (`xn--`).
  if (input === '') return 'xn--';

  const marker = input.lastIndexOf('-');
  const basic = marker > 0 ? input.slice(0, marker) : '';
  // Базовая часть обязана быть ASCII: иначе это не punycode, а совпадение.
  for (const ch of basic) if (ch.codePointAt(0)! > 0x7f) return `xn--${input}`;

  const output: number[] = [...basic].map((ch) => ch.codePointAt(0)!);
  let n = INITIAL_N;
  let bias = INITIAL_BIAS;
  let i = 0;
  let at = marker > 0 ? marker + 1 : 0;

  while (at < input.length) {
    const previous = i;
    let weight = 1;
    for (let k = BASE; ; k += BASE) {
      if (at >= input.length) return `xn--${input}`;
      const code = input.charCodeAt(at++);
      let digit: number;
      if (code >= 0x30 && code <= 0x39) digit = code - 0x30 + 26;
      else if (code >= 0x61 && code <= 0x7a) digit = code - 0x61;
      else if (code >= 0x41 && code <= 0x5a) digit = code - 0x41;
      else return `xn--${input}`;
      i += digit * weight;
      const t = k <= bias ? TMIN : k >= bias + TMAX ? TMAX : k - bias;
      if (digit < t) break;
      weight *= BASE - t;
    }
    const length = output.length + 1;
    bias = adapt(i - previous, length, previous === 0);
    n += Math.floor(i / length);
    i %= length;
    if (n > 0x10ffff) return `xn--${input}`;
    output.splice(i, 0, n);
    i += 1;
  }
  // Разобралось «успешно», но получились управляющие знаки — значит на входе
  // был не punycode, а что-то на него похожее. Показывать человеку U+0080
  // хуже, чем исходную метку: по метке хотя бы видно, что пришло.
  if (output.some((point) => point < 0x20 || (point >= 0x7f && point <= 0x9f))) {
    return `xn--${input}`;
  }
  return String.fromCodePoint(...output);
}
